import * as THREE from 'three';
import { Generator, GLSL_COMMON } from './Generator.js';

const VERT = /* glsl */ `
  varying vec2 vUv;
  void main() { vUv = uv; gl_Position = vec4(position.xy, 0.0, 1.0); }
`;

const FRAG = /* glsl */ `
  varying vec2 vUv;
  uniform sampler2D uMask;
  uniform float uLoopPhase;
  uniform float uBeatPhase;
  uniform float uBeatIndex;
  uniform vec2  uResolution;
  uniform vec3  uColor;
  uniform vec3  uBg;
  uniform float uCell;
  uniform float uPulse;
  uniform float uDecay;
  uniform float uNoise;
  uniform float uLineTh;
  uniform float uFill;
  uniform float uSoftness;
  uniform float uDrift;
  uniform float uCore;
  uniform float uCloud;
  uniform float uHalo;
  uniform float uSpread;

  ${GLSL_COMMON}

  void main() {
    vec2 frag = vUv * uResolution;
    vec2 cellF = frag / uCell;
    vec2 cellIdx = floor(cellF);
    vec2 g = fract(cellF) * uCell;
    vec2 cellCenter = (cellIdx + 0.5) * uCell;
    vec2 sampleUv = cellCenter / uResolution;
    vec2 texel = vec2(uCell / uResolution.x, uCell / uResolution.y);

    float lineD = min(min(g.x, uCell - g.x), min(g.y, uCell - g.y));
    float line = smoothstep(uLineTh, 0.0, lineD) * 0.10;

    float mask = texture2D(uMask, sampleUv).r;
    float edgeMask = smoothstep(0.5 - uSoftness, 0.5 + uSoftness, mask);
    float nearMask = 0.0;
    nearMask = max(nearMask, texture2D(uMask, sampleUv + vec2( texel.x, 0.0)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + vec2(-texel.x, 0.0)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + vec2(0.0,  texel.y)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + vec2(0.0, -texel.y)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + texel * vec2( 2.0,  1.0)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + texel * vec2(-2.0,  1.0)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + texel * vec2( 2.0, -1.0)).r);
    nearMask = max(nearMask, texture2D(uMask, sampleUv + texel * vec2(-2.0, -1.0)).r);
    float haloMask = max(0.0, nearMask - edgeMask);

    float beatPulse = exp(-uBeatPhase * mix(1.5, 10.0, uDecay));
    float slowWave = 0.5 + 0.5 * sin(uLoopPhase * 6.28318530718 + cellIdx.x * 0.09 + cellIdx.y * 0.13);
    float drift = sin(uLoopPhase * 6.28318530718 * 0.5 + cellIdx.y * 0.07) * uDrift;
    float cellNoise = hash21(cellIdx + vec2(uBeatIndex * 0.73, 17.0));
    float noisePulse = smoothstep(1.0 - uNoise, 1.0, cellNoise) * beatPulse;
    float cloudNoise = hash21(cellIdx * vec2(0.91, 1.13) + vec2(31.0, uBeatIndex * 0.37));
    float cloudPulse = smoothstep(1.0 - uCloud * (0.35 + beatPulse * 0.65), 1.0, cloudNoise);
    float ambientNoise = hash21(cellIdx * vec2(0.37, 0.63) + vec2(7.0, uLoopPhase * 19.0));
    float ambientWave = 0.5 + 0.5 * sin(uLoopPhase * 6.28318530718 * 0.5 + cellIdx.x * 0.035 + cellIdx.y * 0.02);
    float farCloud = smoothstep(1.0 - uSpread * (0.18 + beatPulse * 0.22), 1.0, ambientNoise) * ambientWave;

    float coreFill = edgeMask * (
      uCore * (0.52 + slowWave * 0.18) +
      uFill * (0.55 + slowWave * 0.45) +
      uPulse * beatPulse * (0.65 + slowWave * 0.35) +
      noisePulse * 0.65 +
      drift * 0.08
    );
    float cloudFill = haloMask * (
      uHalo * (0.15 + beatPulse * 0.25 + slowWave * 0.15) +
      cloudPulse * (0.10 + beatPulse * 0.35) +
      uNoise * cloudNoise * 0.08
    );
    float ambientFill = (1.0 - edgeMask) * (
      farCloud * 0.18 +
      uSpread * uFill * 0.08 * ambientWave
    );

    float glow = edgeMask * (uCore * 0.18 + uPulse * beatPulse * 0.35 + uNoise * cellNoise * 0.12);
    float cloudGlow = haloMask * (uHalo * 0.12 + beatPulse * 0.10);
    float ambientGlow = (1.0 - edgeMask) * uSpread * farCloud * (0.08 + beatPulse * 0.08);
    float intensity = max(line, coreFill + glow + cloudFill + cloudGlow + ambientFill + ambientGlow);
    vec3 col = mix(uBg, uColor, clamp(intensity, 0.0, 1.0));
    gl_FragColor = vec4(col, 1.0);
  }
`;

export class WordGrid extends Generator {
  constructor() {
    super({
      name: 'Word Grid',
      category: 'ambient',
      schema: {
        text:      { default: 'AI DAY', label: 'Text' },
        cell:      { default: 34, min: 6, max: 220, step: 1, label: 'Cell size (px)' },
        bpm:       { default: 60, min: 20, max: 180, step: 1, label: 'BPM' },
        pulse:     { default: 0.85, min: 0, max: 2, step: 0.01, label: 'Pulse' },
        decay:     { default: 0.22, min: 0, max: 1, step: 0.01, label: 'Decay' },
        noise:     { default: 0.18, min: 0, max: 1, step: 0.01, label: 'Noise' },
        drift:     { default: 0.22, min: 0, max: 1, step: 0.01, label: 'Drift' },
        fill:      { default: 0.18, min: 0, max: 1, step: 0.01, label: 'Base fill' },
        lineTh:    { default: 1.0, min: 0.2, max: 6, step: 0.1, label: 'Line thickness' },
        softness:  { default: 0.16, min: 0.01, max: 0.45, step: 0.01, label: 'Letter softness' },
        core:      { default: 0.82, min: 0, max: 1.5, step: 0.01, label: 'Core on' },
        cloud:     { default: 0.42, min: 0, max: 1, step: 0.01, label: 'Cloud activity' },
        halo:      { default: 0.38, min: 0, max: 1.5, step: 0.01, label: 'Halo' },
        spread:    { default: 0.42, min: 0, max: 1.5, step: 0.01, label: 'Canvas spread' },
        textWidth: { default: 0.96, min: 0.1, max: 1.4, step: 0.01, label: 'Text width' },
        textHeight:{ default: 0.72, min: 0.1, max: 1.2, step: 0.01, label: 'Text height' },
        accent:    { default: '#87ffd7', type: 'color', label: 'Color' },
        background:{ default: '#030405', type: 'color', label: 'Background' },
      }
    });
  }

  onEnter(scene, engine) {
    this.engine = engine;

    this.off = document.createElement('canvas');
    this.offCtx = this.off.getContext('2d', { alpha: false, willReadFrequently: true });
    this.off.width = engine.width;
    this.off.height = engine.height;

    this.tex = new THREE.CanvasTexture(this.off);
    this.tex.minFilter = THREE.LinearFilter;
    this.tex.magFilter = THREE.LinearFilter;
    this.tex.generateMipmaps = false;

    const geo = new THREE.PlaneGeometry(2, 2);
    const mat = new THREE.ShaderMaterial({
      vertexShader: VERT,
      fragmentShader: FRAG,
      uniforms: {
        uMask:       { value: this.tex },
        uLoopPhase:  { value: 0 },
        uBeatPhase:  { value: 0 },
        uBeatIndex:  { value: 0 },
        uResolution: { value: new THREE.Vector2(engine.width, engine.height) },
        uColor:      { value: new THREE.Color(this.v('accent')) },
        uBg:         { value: new THREE.Color(this.v('background')) },
        uCell:       { value: this.v('cell') },
        uPulse:      { value: this.v('pulse') },
        uDecay:      { value: this.v('decay') },
        uNoise:      { value: this.v('noise') },
        uLineTh:     { value: this.v('lineTh') },
        uFill:       { value: this.v('fill') },
        uSoftness:   { value: this.v('softness') },
        uDrift:      { value: this.v('drift') },
        uCore:       { value: this.v('core') },
        uCloud:      { value: this.v('cloud') },
        uHalo:       { value: this.v('halo') },
        uSpread:     { value: this.v('spread') },
      },
      transparent: false,
      depthWrite: false,
      depthTest: false
    });

    this.mesh = new THREE.Mesh(geo, mat);
    this.mesh.frustumCulled = false;
    scene.add(this.mesh);
    this.track(this.mesh);
    this.track(this.tex);
    this._redrawMask();
  }

  onResize(w, h) {
    if (this.mesh) {
      this.mesh.material.uniforms.uResolution.value.set(w, h);
    }
    if (this.off) {
      this.off.width = w;
      this.off.height = h;
      this._redrawMask();
    }
  }

  onParamChange(key, val) {
    if (!this.mesh) return;
    const u = this.mesh.material.uniforms;
    if (key === 'accent') {
      u.uColor.value.set(val);
      return;
    }
    if (key === 'background') {
      u.uBg.value.set(val);
      this._redrawMask();
      return;
    }
    if (key === 'text' || key === 'textWidth' || key === 'textHeight') {
      this._redrawMask();
      return;
    }
    const uniformKey = `u${key[0].toUpperCase()}${key.slice(1)}`;
    if (uniformKey in u) u[uniformKey].value = val;
  }

  _redrawMask() {
    if (!this.offCtx || !this.tex) return;
    const ctx = this.offCtx;
    const w = this.off.width;
    const h = this.off.height;
    const text = String(this.v('text') || 'AI DAY').trim() || 'AI DAY';
    const lines = text.split('|').map(line => line.trim()).filter(Boolean);
    const finalLines = lines.length ? lines : ['AI DAY'];

    ctx.fillStyle = '#000000';
    ctx.fillRect(0, 0, w, h);

    const targetWidth = w * Math.min(1.2, this.v('textWidth'));
    const blockHeight = h * Math.min(0.95, this.v('textHeight'));
    const fontPx = Math.max(24, Math.floor(
      blockHeight / Math.max(1, finalLines.length * 0.92)
    ));

    ctx.fillStyle = '#ffffff';
    ctx.font = `400 ${fontPx}px 'Pumpa', 'JetBrains Mono', ui-monospace, monospace`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';

    const metrics = finalLines.map(line => ctx.measureText(line));
    const measuredWidth = Math.max(1, ...metrics.map(metric => metric.width));
    const scaleX = Math.max(0.15, targetWidth / measuredWidth);

    const maxAscent = Math.max(1, ...metrics.map(metric => metric.actualBoundingBoxAscent || fontPx * 0.75));
    const maxDescent = Math.max(1, ...metrics.map(metric => metric.actualBoundingBoxDescent || fontPx * 0.2));
    const lineHeight = maxAscent + maxDescent;
    const totalHeight = lineHeight * finalLines.length;
    const baselineY = h * 0.5 - totalHeight * 0.5 + maxAscent;

    const temp = this._maskTemp || document.createElement('canvas');
    if (!this._maskTemp) this._maskTemp = temp;
    temp.width = w;
    temp.height = h;
    const tempCtx = temp.getContext('2d', { alpha: false, willReadFrequently: true });
    tempCtx.fillStyle = '#000000';
    tempCtx.fillRect(0, 0, w, h);
    tempCtx.fillStyle = '#ffffff';
    tempCtx.font = ctx.font;
    tempCtx.textAlign = 'center';
    tempCtx.textBaseline = 'alphabetic';
    tempCtx.save();
    tempCtx.translate(w * 0.5, 0);
    tempCtx.scale(scaleX, 1);
    for (let i = 0; i < finalLines.length; i++) {
      tempCtx.fillText(finalLines[i], 0, baselineY + i * lineHeight);
    }
    tempCtx.restore();

    const image = tempCtx.getImageData(0, 0, w, h).data;
    let minY = h;
    let maxY = -1;
    for (let y = 0; y < h; y++) {
      const rowOffset = y * w * 4;
      for (let x = 0; x < w; x++) {
        if (image[rowOffset + x * 4] > 0) {
          if (y < minY) minY = y;
          if (y > maxY) maxY = y;
          break;
        }
      }
    }

    let shiftY = 0;
    if (maxY >= minY) {
      const boundsCenterY = (minY + maxY + 1) * 0.5;
      shiftY = Math.round(h * 0.5 - boundsCenterY);
    }

    ctx.save();
    ctx.drawImage(temp, 0, shiftY);
    ctx.restore();

    this.tex.needsUpdate = true;
  }

  update(dt, time, engine) {
    if (!this.mesh) return;
    const loop = engine.getLoopInfo(time);
    const u = this.mesh.material.uniforms;
    u.uLoopPhase.value = loop.phase;
    u.uBeatPhase.value = loop.beatPhase;
    u.uBeatIndex.value = loop.beatIndex;
  }
}
